import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { MatDialogRef } from '@angular/material/dialog'
import {
  ConfirmDialogService,
  ConfirmDialogStatus,
  convertToTokeetEmail,
  currencies,
  notAllSpacesValidator,
  rentalEmailPrefixFromName,
  RentalEmailValidator,
  SaveForm,
} from '@tokeet-frontend/tv3-platform'
import { countries } from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import { addRental, selectCurrentAccount, selectSubdomain } from '@tokeet-frontend/tv3-platform'
import { RentalEmailNotTakenValidator } from '@tokeet-frontend/tv3-platform'
import { RentalNameNotTakenValidator } from '@tokeet-frontend/tv3-platform'
import { notAllNumbersValidator } from '@tokeet-frontend/tv3-platform'
import { ParsedGooglePlace } from '@tokeet-frontend/tv3-platform'
import { Router } from '@angular/router'
import { AccountGuard, SubdomainGuard } from '@tokeet-frontend/tv3-platform'
import { DataCheckerService } from '@tokeet-frontend/tv3-platform'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { selectActiveTokeetPlanView } from '@tv3/store/plan/plan.selectors'
import { UserStorage, defaultRentalColor } from '@tokeet-frontend/tv3-platform'
import { of } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { isTokeetStandaloneVersion } from '@tv3/store/plan/plan.view'

@Component({
  selector: 'app-add-rental-dialog',
  templateUrl: './add-rental-dialog.component.html',
  styleUrls: ['./add-rental-dialog.component.scss'],
})
export class AddRentalDialogComponent extends Destroyable implements OnInit {
  countryList = countries

  addressForm = this.fb.group({
    address: ['', [Validators.minLength(2), Validators.maxLength(140), notAllSpacesValidator]],
    city: ['', [Validators.minLength(2), Validators.maxLength(50), Validators.required, notAllSpacesValidator]],
    state: ['', [Validators.minLength(2), Validators.maxLength(50), notAllSpacesValidator]],
    zip: ['', [Validators.minLength(2), Validators.maxLength(50), notAllSpacesValidator]],
    country: [],
    country_code: [],
  })

  form = this.fb.group({
    name: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(140),
        notAllNumbersValidator,
        notAllSpacesValidator,
      ],
      RentalNameNotTakenValidator.createValidator(this.store),
    ],
    nickname: ['', [Validators.minLength(2), Validators.maxLength(140), notAllNumbersValidator, notAllSpacesValidator]],
    phone: ['', [Validators.required, notAllSpacesValidator]],
    email: [
      '',
      [Validators.required, Validators.minLength(3)],
      [RentalEmailNotTakenValidator.createValidator(this.store), RentalEmailValidator.createValidator(this.store)],
    ],
    currency: [null, [Validators.required]],
    color: [defaultRentalColor],
    address: this.addressForm,
    registration: [],
    gps: [{}],
  })

  get gpsCtrl() {
    return this.form.get('gps') as FormControl
  }

  subDomain: string

  isStandaloneTokeetSubscription = false

  constructor(
    protected fb: FormBuilder,
    protected store: Store<any>,
    private router: Router,
    private storage: UserStorage,
    private confirm: ConfirmDialogService,
    private dataCheckerService: DataCheckerService,
    public dialogRef: MatDialogRef<AddRentalDialogComponent>,
    private amplitudeService: AmplitudeService
  ) {
    super()
    this.dataCheckerService.check([SubdomainGuard, AccountGuard])
  }

  ngOnInit() {
    this.store.pipe(select(selectActiveTokeetPlanView), untilDestroy(this)).subscribe((sub) => {
      this.isStandaloneTokeetSubscription = sub ? isTokeetStandaloneVersion(sub.version) : false
    })

    const currencyCtrl = this.form.get('currency') as FormControl
    this.store.pipe(select(selectCurrentAccount), untilDestroy(this)).subscribe((account) => {
      if (!currencyCtrl.dirty) {
        currencyCtrl.patchValue(account?.currency || currencies[0], { emitEvent: false })
      }
    })

    this.store.pipe(select(selectSubdomain), untilDestroy(this)).subscribe((subDomain) => {
      this.subDomain = subDomain
    })
  }

  goToAccountSettings() {
    this.router.navigate(['/account'])
    this.close()
  }

  goToConnections() {
    this.router.navigate(['/channels'])
    this.close()
  }

  onNameChange(name: string) {
    this.form.patchValue({ email: rentalEmailPrefixFromName(name) })
  }

  onPlaceChange(parsedPlace: ParsedGooglePlace) {
    const country = R.find((c) => c.id === parsedPlace.countryShort, this.countryList)
    this.addressForm.patchValue({
      address: (parsedPlace.streetNumber ? parsedPlace.streetNumber + ' ' : '') + (parsedPlace.street || ''),
      city: parsedPlace.city,
      state: parsedPlace.state,
      country: country?.name,
      country_code: country?.id,
      zip: parsedPlace.postCode,
    })

    this.gpsCtrl.patchValue(parsedPlace.gps)
  }

  onCityChange(parsedPlace: ParsedGooglePlace) {
    const { city } = this.addressForm.getRawValue()

    const googleCity = (parsedPlace.city || '')?.trim()
    this.addressForm.patchValue({
      city: googleCity ? googleCity : city,
    })

    const country = R.find((c: any) => c.id === parsedPlace.countryShort, this.countryList)
    if (country) {
      this.addressForm.patchValue({
        country: country.name,
        country_code: country.id,
      })
    }

    this.gpsCtrl.patchValue(parsedPlace.gps)
  }

  close() {
    this.dialogRef.close()
  }

  showPricingWarning() {
    const disableNotifyKey = 'disable-create-rental-cost-notification'
    if (this.isStandaloneTokeetSubscription && !this.storage.get(disableNotifyKey)) {
      return this.confirm
        .confirmExtra(
          {
            title: $localize`Create Rental`,
            body: $localize`Creating a Rental will increase your pricing. Consult the billing info page for more details on cost.`,
            cancelText: $localize`MOVE TO BILLING INFO`,
            cancelBtnClass: 'btn btn-secondary-info',
            confirmText: $localize`CREATE RENTAL`,
            extra: $localize`Disable this notification forever`,
            defaultExtraValue: false,
          },
          true
        )
        .pipe(
          tap(({ isChecked }) => {
            this.storage.set(disableNotifyKey, isChecked)
          }),
          map(({ status }) => status)
        )
    }
    return of(ConfirmDialogStatus.Confirmed)
  }

  @SaveForm()
  save(form: FormGroup) {
    const { registration, ...payload } = form.getRawValue()
    payload.email = convertToTokeetEmail(payload.email, this.subDomain)
    payload.attributes = { registration }

    const doAction = () => {
      this.amplitudeService.logEvent('add-rental-manually')
      this.store.dispatch(addRental({ payload }))
      this.close()
    }

    this.showPricingWarning().subscribe((status) => {
      if (status === ConfirmDialogStatus.Canceled) {
        this.close()
        this.router.navigate(['/billing/info'])
      } else if (status === ConfirmDialogStatus.Confirmed) {
        doAction()
      }
    })
  }
}
